"use client";

// V2 Step 10d — returning-user migration modal.
//
// Fires when a user has profile.onboarded=true but profile.onboarding_completed_v2=false
// AND is on Chromium desktop AND has no directoryHandle restored. Trigger logic lives
// in components/AppShell.js.
//
// Hard block: no skip button, no close button, no escape key, no overlay-click-to-dismiss.
// Single-screen UX matching V2 spec § Returning user migration paths (line 1529+).
//
// On successful folder pick: lockstep writes (mirror_folder_connected + storage_mode +
// onboarding_completed_v2) match the same shape as Step 10b + 10c.

import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useVaultContext } from "../lib/vault";
import { supabase } from "../lib/supabase";
import { pickDirectory } from "../lib/vault-local";
import { emitSignal } from "../lib/signal";

// V2 spec failure-path copy (matches 10b's onPickDirectory error handling)
const V2_PICKER_FAILURE = "Need a destination — I can't remember any of this without one. Try again, or check your browser permissions.";

export default function V2MigrationModal() {
  const { user, fetchProfile } = useAuth();
  const vaultCtx = useVaultContext();
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const handlePickDestination = async () => {
    if (!user?.id || busy) return;
    setBusy(true);
    setErrorMsg(null);
    let handle = null;
    try {
      handle = await pickDirectory();
    } catch (e) {
      if (e?.name !== "AbortError") {
        emitSignal("vault_returning_user_pick_failed", "warning", { error: e?.message || null });
        setErrorMsg(V2_PICKER_FAILURE);
      }
      // AbortError = user cancelled → silent return, hard block holds
      setBusy(false);
      return;
    }
    if (!handle) {
      // No handle returned, no error → user cancelled silently
      setBusy(false);
      return;
    }

    try {
      // V2 Step 14d — single source-of-truth post-collapse. Lockstep
      // simplified: setConnectedDirectory + setMirrorFolderConnected.
      // setStorageMode call removed (callback no longer exists).
      // onboarding_completed_v2 last so partial-migration state stays
      // detectable via profile flag if the folder commit succeeds but
      // profile update fails.
      await vaultCtx.setConnectedDirectory(handle);
      await vaultCtx.setMirrorFolderConnected(true);
      await supabase
        .from("profiles")
        .update({ onboarding_completed_v2: true })
        .eq("id", user.id);
      emitSignal("vault_returning_user_completed", "info", { auto_detected: false });
      // Refresh profile triggers AppShell re-render → modal unmounts naturally
      // because needsMigration becomes false.
      await fetchProfile(user.id);
    } catch (e) {
      emitSignal("vault_returning_user_commit_failed", "warning", { error: e?.message || null });
      setErrorMsg(e?.message || "Couldn't set up that folder. Try another?");
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(42, 40, 38, 0.85)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--space-6)",
      }}
      // Non-skippable: no onClick handler. Overlay click does nothing.
      role="dialog"
      aria-modal="true"
      aria-labelledby="v2-migration-headline"
    >
      <div
        style={{
          maxWidth: 520,
          width: "100%",
          background: "var(--color-bg)",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius-lg)",
          padding: "var(--space-8)",
          textAlign: "left",
        }}
      >
        <h2
          id="v2-migration-headline"
          style={{
            fontSize: "var(--font-size-2xl)",
            fontWeight: "var(--font-weight-bold)",
            lineHeight: "var(--line-height-snug)",
            letterSpacing: "var(--letter-spacing-tight)",
            marginBottom: "var(--space-4)",
            textAlign: "center",
            textWrap: "balance",
          }}
        >
          Welcome back.
        </h2>
        <p
          style={{
            fontSize: "var(--font-size-base)",
            color: "var(--color-text-muted)",
            lineHeight: "var(--line-height-relaxed)",
            marginBottom: "var(--space-6)",
            textAlign: "center",
            textWrap: "balance",
          }}
        >
          I improved how I work while you were away — got faster, smarter about your stuff. Just need to connect your vault folder so I can pick up where we left off.
        </p>
        {errorMsg && (
          <p
            style={{
              color: "var(--color-error, #c04a2a)",
              fontSize: "var(--font-size-sm)",
              textAlign: "center",
              marginBottom: "var(--space-4)",
              lineHeight: "var(--line-height-relaxed)",
            }}
          >
            {errorMsg}
          </p>
        )}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <button
            onClick={handlePickDestination}
            disabled={busy}
            style={{
              padding: "var(--space-2-5) var(--space-5)",
              background: busy ? "var(--color-border-light)" : "var(--color-accent)",
              color: busy ? "var(--color-text-dim)" : "var(--color-text-inverse)",
              border: "none",
              borderRadius: "var(--radius-md)",
              fontSize: "var(--font-size-sm)",
              fontWeight: "var(--font-weight-semibold)",
              fontFamily: "var(--font-primary)",
              cursor: busy ? "not-allowed" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "var(--space-2)",
            }}
          >
            {busy ? "Picking…" : "Pick Destination"}
            <ArrowRight size={14} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
